import { Component, computed, input } from '@angular/core';
import { type WeekCardStatus } from './week-card.model';

@Component({
  selector: 'lib-week-card',
  standalone: true,
  host: {
    '[class]': 'hostClasses()',
    'type': 'button',
  },
  template: `
    <div class="msa-wc__header">
      <div>
        <p class="msa-wc__day">{{ day() }}</p>
        <p class="msa-wc__date">{{ date() }}</p>
      </div>
      <span class="msa-wc__badge" [style.background]="badgeBg()" [style.color]="badgeText()">
        {{ occupancy() }}%
      </span>
    </div>
    <div class="msa-wc__progress-track">
      <div class="msa-wc__progress-fill" [style.width.%]="occupancy()"></div>
    </div>
    <div class="msa-wc__stats">
      <div class="msa-wc__stat">
        <p class="msa-wc__stat-label">Reservas</p>
        <p class="msa-wc__stat-value">{{ reservations() }}</p>
      </div>
      <div class="msa-wc__stat">
        <p class="msa-wc__stat-label">Ingresos</p>
        <p class="msa-wc__stat-value">{{ revenue() }}</p>
      </div>
    </div>
  `,
  styles: [`
    /* ═══════════════════════════════════════════════
       MSA WeekCard Compound
       ═══════════════════════════════════════════════
       Day summary card: progress bar, badge, stats.
       All styles on :host / :host(.class).
       ═══════════════════════════════════════════════ */

    :host {
      display: flex;
      flex-direction: column;
      text-align: left;
      border: 1px solid #f1f5f9;
      border-radius: 12px;
      background: #fff;
      padding: 14px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: inherit;
      box-sizing: border-box;
    }

    :host(:hover) {
      border-color: #e2e8f0;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      transform: translateY(-1px);
    }

    :host(.msa-wc--active) {
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
    }

    /* ──────────────────────────────────────
       Header
       ────────────────────────────────────── */
    .msa-wc__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;
    }

    .msa-wc__day {
      margin: 0;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #9ca3af;
    }

    .msa-wc__date {
      margin: 2px 0 0;
      font-size: 13px;
      font-weight: 700;
      color: #0b1c33;
    }

    /* ──────────────────────────────────────
       Badge
       ────────────────────────────────────── */
    .msa-wc__badge {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      border-radius: 9999px;
      font-size: 10px;
      font-weight: 600;
      white-space: nowrap;
      flex-shrink: 0;
    }

    /* ──────────────────────────────────────
       Progress bar
       ────────────────────────────────────── */
    .msa-wc__progress-track {
      margin-top: 16px;
      height: 8px;
      border-radius: 9999px;
      background: #f3f4f6;
      overflow: hidden;
    }

    .msa-wc__progress-fill {
      height: 100%;
      border-radius: 9999px;
      background: #2563eb;
      transition: width 0.3s ease;
    }

    /* ──────────────────────────────────────
       Stats grid
       ────────────────────────────────────── */
    .msa-wc__stats {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      margin-top: 12px;
      font-size: 11.5px;
    }

    .msa-wc__stat {
      background: #f9fafb;
      border-radius: 8px;
      padding: 8px 10px;
    }

    .msa-wc__stat-label {
      margin: 0;
      color: #9ca3af;
    }

    .msa-wc__stat-value {
      margin: 2px 0 0;
      font-weight: 700;
      color: #0b1c33;
    }
  `],
})
export class WeekCardComponent {
  /** Day abbreviation (e.g. "Mié"). */
  readonly day = input.required<string>();

  /** Date string (e.g. "15 Oct"). */
  readonly date = input.required<string>();

  /** Occupancy percentage 0–100. */
  readonly occupancy = input.required<number>();

  /** Number of reservations. */
  readonly reservations = input.required<number>();

  /** Formatted revenue (e.g. "$1.25M"). */
  readonly revenue = input.required<string>();

  /** Status drives badge colors. */
  readonly status = input<WeekCardStatus>('disponible');

  /** Highlighted/active state. */
  readonly active = input<boolean>(false);

  /** Host classes. */
  readonly hostClasses = computed(() => ({
    'msa-wc': true,
    'msa-wc--active': this.active(),
  }));

  /** Badge background color from status. */
  readonly badgeBg = computed(() => {
    const map: Record<WeekCardStatus, string> = {
      disponible:    '#dff3e6',
      confirmada:    '#d6e6fd',
      pendiente:     '#fbecc4',
      torneo:        '#e6dbfb',
      mantenimiento: '#dde1e6',
      bloqueada:     '#fecaca',
    };
    return map[this.status()];
  });

  /** Badge text color from status. */
  readonly badgeText = computed(() => {
    const map: Record<WeekCardStatus, string> = {
      disponible:    '#1e8a4c',
      confirmada:    '#1d5fd6',
      pendiente:     '#a6790b',
      torneo:        '#7c3fd1',
      mantenimiento: '#6b7280',
      bloqueada:     '#dc2626',
    };
    return map[this.status()];
  });
}
